const LOGO_IMAGE_MIMES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const MAX_ORIGINAL_BYTES = 20 * 1024 * 1024;
const MIN_SIZE_REDUCTION = 0.6;
const MIN_STRICT_ORIGINAL_BYTES = 50 * 1024;
const MAX_EDGE = 2048;
const MAX_COMPRESS_ITERS = 40;

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) reject(new Error('Falha ao processar imagem.'));
        else resolve(blob);
      },
      type,
      quality
    );
  });
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Arquivo de imagem inválido.'));
    };
    img.src = url;
  });
}

/** Detecta se o browser consegue emitir WebP via canvas (mantém alfa + qualidade). */
async function canEncodeWebp() {
  try {
    const c = document.createElement('canvas');
    c.width = 1;
    c.height = 1;
    const blob = await canvasToBlob(c, 'image/webp', 0.8);
    return Boolean(blob && blob.type === 'image/webp' && blob.size > 0);
  } catch {
    return false;
  }
}

/**
 * Comprime a logo do App Go:
 * - somente JPEG/PNG/WebP de entrada
 * - PNG/WebP: preserva transparência (WebP com qualidade, ou PNG só com escala)
 * - JPEG: saída JPEG com fundo branco
 * - máximo 20MB no arquivo original
 * - redução de tamanho ≥ 60% (saída ≤ 40% do original), quando o original ≥ 50KB
 * - mantém proporção; limita o maior lado a 2048px
 */
export async function compressGoBrandLogo(file) {
  if (!file || !(file instanceof Blob)) {
    throw new Error('Selecione uma imagem.');
  }
  if (!LOGO_IMAGE_MIMES.has(file.type)) {
    throw new Error('Formato não permitido. Use JPEG, PNG ou WebP.');
  }
  if (file.size > MAX_ORIGINAL_BYTES) {
    throw new Error('Imagem muito grande. Máximo 20MB.');
  }

  const originalSize = file.size;
  const targetMax =
    originalSize >= MIN_STRICT_ORIGINAL_BYTES
      ? Math.floor(originalSize * (1 - MIN_SIZE_REDUCTION))
      : MAX_ORIGINAL_BYTES;

  const img = await loadImage(file);
  let w = img.naturalWidth || img.width;
  let h = img.naturalHeight || img.height;
  if (!w || !h) throw new Error('Dimensões da imagem inválidas.');

  const preserveAlpha = file.type === 'image/png' || file.type === 'image/webp';
  const webpOk = preserveAlpha ? await canEncodeWebp() : false;
  const outputMime = preserveAlpha ? (webpOk ? 'image/webp' : 'image/png') : 'image/jpeg';
  const ext = outputMime === 'image/webp' ? 'webp' : outputMime === 'image/png' ? 'png' : 'jpg';
  const usesQuality = outputMime !== 'image/png';

  const longest = Math.max(w, h);
  let scale = longest > MAX_EDGE ? MAX_EDGE / longest : 1;
  let quality = 0.85;

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d', { alpha: preserveAlpha });

  const draw = (s) => {
    const cw = Math.max(1, Math.round(w * s));
    const ch = Math.max(1, Math.round(h * s));
    canvas.width = cw;
    canvas.height = ch;
    if (preserveAlpha) {
      ctx.clearRect(0, 0, cw, ch);
    } else {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, cw, ch);
    }
    ctx.drawImage(img, 0, 0, cw, ch);
  };

  draw(scale);
  let blob = await canvasToBlob(canvas, outputMime, usesQuality ? quality : undefined);
  let iters = 0;

  while (blob.size > targetMax && iters < MAX_COMPRESS_ITERS) {
    iters += 1;
    if (usesQuality && quality > 0.35) {
      quality = Math.max(0.35, quality - 0.08);
    } else if (scale > 0.35) {
      scale = Math.max(0.35, scale * 0.85);
      draw(scale);
    } else {
      break;
    }
    blob = await canvasToBlob(canvas, outputMime, usesQuality ? quality : undefined);
  }

  if (originalSize >= MIN_STRICT_ORIGINAL_BYTES && blob.size > targetMax) {
    throw new Error('Não foi possível reduzir a imagem em 60%. Tente outro arquivo.');
  }

  return new File([blob], `vervego.${ext}`, { type: outputMime, lastModified: Date.now() });
}

export const GO_BRAND_LOGO_ACCEPT = 'image/jpeg,image/png,image/webp';
export const GO_BRAND_LOGO_MAX_BYTES = MAX_ORIGINAL_BYTES;
